import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ShieldCheck, FileText, Link as LinkIcon, Paperclip, ListChecks,
    Download, ArrowLeft, Copy, ExternalLink, Loader2, CircleDot,
    CheckCircle2, XCircle, AlertTriangle, Clock, Info, Code,
    Mail, Building2, User, Calendar, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import TechnicalStatusBadge from "../components/TechnicalStatusBadge";
import ReviewStatusBadge from "../components/ReviewStatusBadge";
import ScoreGauge from "../components/ScoreGauge";
import RiskDimensionCard from "../components/RiskDimensionCard";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import AlertBanner from "../components/AlertBanner";
import SignatureVerificationBadge from "../components/SignatureVerificationBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { downloadAuthenticated } from "../lib/download";

const STATE_ICONS = {
    pending: { icon: CircleDot, cls: "text-[#94A3B8]" },
    running: { icon: Loader2, cls: "text-[#2563EB] animate-spin" },
    done: { icon: CheckCircle2, cls: "text-[#16A34A]" },
    error: { icon: XCircle, cls: "text-[#DC2626]" },
    skipped: { icon: Clock, cls: "text-[#F59E0B]" },
};

function formatDT(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    } catch { return iso; }
}

function shortHash(h) {
    if (!h) return "—";
    return h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-8)}` : h;
}

export default function PecAnalysisPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [bodyMode, setBodyMode] = useState("text");
    const [risk, setRisk] = useState(null);
    const [dimensions, setDimensions] = useState(null);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [ruleExecs, setRuleExecs] = useState([]);
    const [rulesInfo, setRulesInfo] = useState([]);
    const pollTimerRef = useRef(null);

    const loadRisk = async () => {
        try {
            const { data } = await api.get(`/pec/${id}/risk`);
            setRisk(data.risk_scores);
            setDimensions(data.dimensions || null);
            setAiAnalysis(data.ai_analysis);
            setRuleExecs(data.rule_executions || []);
            setRulesInfo(data.rules || []);
        } catch (_) { }
    };

    const loadFull = async () => {
        try {
            const { data } = await api.get(`/pec/${id}`);
            setData(data);
            setStatus({
                pipeline: data.pec.pipeline,
                pipeline_status: data.pec.pipeline_status,
                technical_status: data.pec.technical_status,
                technical_score: data.pec.technical_score,
            });
            await loadRisk();
            return data;
        } catch (err) {
            setError(err?.response?.data?.detail || "Errore");
        } finally {
            setLoading(false);
        }
    };

    const pollStatus = async () => {
        try {
            const { data: s } = await api.get(`/pec/${id}/status`);
            setStatus(s);
            if (s.pipeline_status === "done" || s.pipeline_status === "skipped") {
                // final data now — refetch
                await loadFull();
                if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            }
        } catch (_) { }
    };

    useEffect(() => {
        setLoading(true);
        loadFull().then((d) => {
            if (d && d.pec.pipeline_status !== "done" && d.pec.pipeline_status !== "skipped") {
                pollTimerRef.current = setInterval(pollStatus, 1000);
            }
        });
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) return <LoadingState label="Caricamento analisi..." />;
    if (error) return <ErrorState title="Impossibile caricare l'analisi" description={error} onRetry={() => window.location.reload()} />;
    if (!data) return null;

    const pec = data.pec;
    const attachments = data.attachments || [];
    const links = data.links || [];
    const checks = data.technical_checks || [];
    const daticert = data.daticert;
    const headers = data.headers;
    const company = data.company;
    const pipeline = status?.pipeline || pec.pipeline || [];
    const isRunning = (status?.pipeline_status ?? pec.pipeline_status) !== "done" && (status?.pipeline_status ?? pec.pipeline_status) !== "skipped";

    const dlEml = async () => {
        await downloadAuthenticated(`/pec/${id}/raw`, pec.filename || `${id}.eml`);
    };

    return (
        <div className="space-y-6">
            <button
                onClick={() => navigate("/pec/analyses")}
                data-testid="back-to-analyses-btn"
                className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#2563EB] transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Torna all'elenco analisi
            </button>

            {/* Header 2-column */}
            <Card className="p-6 border-[#DCE3EE]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <TechnicalStatusBadge status={pec.technical_status || "In elaborazione"} />
                            <ReviewStatusBadge status={
                                pec.review_status === "nuova" ? "Nuova" :
                                    pec.review_status === "in_revisione" ? "In revisione" :
                                        pec.review_status === "chiusa" ? "Chiusa" : "Nuova"
                            } />
                            {pec.envelope_type && (
                                <Badge variant="outline" className="text-xs">{pec.envelope_type}</Badge>
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-[#172033] leading-tight" data-testid="pec-subject">
                            {pec.subject || "(Senza oggetto)"}
                        </h1>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Mittente</div>
                                <div className="flex items-center gap-1.5 text-[#172033]">
                                    <User className="w-3.5 h-3.5 text-[#64748B]" />
                                    <span className="font-medium">{pec.from_name || "—"}</span>
                                </div>
                                <div className="text-xs text-[#64748B] ml-5" data-testid="pec-from">{pec.from_address || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Destinatari</div>
                                <div className="flex items-center gap-1.5 text-[#172033]">
                                    <Mail className="w-3.5 h-3.5 text-[#64748B]" />
                                    <span className="text-xs">
                                        {(pec.to_addresses || []).map((t) => t.address).join(", ") || "—"}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Data ricezione</div>
                                <div className="flex items-center gap-1.5 text-[#172033]">
                                    <Calendar className="w-3.5 h-3.5 text-[#64748B]" />
                                    <span className="text-sm" data-testid="pec-date">{formatDT(pec.date || pec.created_at)}</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Azienda</div>
                                <div className="flex items-center gap-1.5 text-[#172033]">
                                    <Building2 className="w-3.5 h-3.5 text-[#64748B]" />
                                    <span className="text-sm font-medium">{company?.name || "—"}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 items-stretch lg:items-end">
                        <Button
                            data-testid="export-report-btn"
                            variant="outline"
                            className="justify-center gap-2"
                            onClick={async () => {
                                try {
                                    const { data } = await api.post(`/pec/${pec.id}/report`);
                                    toast.success(`Report ${data.report_id_human} generato`);
                                    await downloadAuthenticated(`/reports/${data.id}/download`, `${data.report_id_human || "report"}.pdf`);
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Errore generazione report");
                                }
                            }}
                        >
                            Esporta report
                        </Button>
                        <Button
                            data-testid="download-eml-btn"
                            variant="outline"
                            className="justify-center gap-2"
                            onClick={dlEml}
                        >
                            <Download className="w-4 h-4" />
                            Scarica .eml
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Pipeline (always visible) */}
            <Card className="p-6 border-[#DCE3EE]" data-testid="pipeline-section">
                <div className="flex items-center gap-2 mb-4">
                    {isRunning ? (
                        <Loader2 className="w-4 h-4 text-[#2563EB] animate-spin" />
                    ) : (
                        <CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
                    )}
                    <h3 className="text-sm font-semibold text-[#172033]">
                        Pipeline di analisi {isRunning ? "in corso" : "completata"}
                    </h3>
                    <span className="ml-auto text-xs text-[#64748B]">
                        {pipeline.filter((s) => s.status === "done").length}/{pipeline.length} step
                    </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid="pipeline-live">
                    {pipeline.map((step) => {
                        const Icon = (STATE_ICONS[step.status] || STATE_ICONS.pending).icon;
                        const cls = (STATE_ICONS[step.status] || STATE_ICONS.pending).cls;
                        return (
                            <div
                                key={step.step}
                                className="flex items-start gap-3 p-3 rounded-lg border border-[#DCE3EE] bg-[#F5F7FB]/40"
                                data-testid={`pipeline-step-${step.step}`}
                            >
                                <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${cls}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-[#94A3B8]">
                                            {String(step.step).padStart(2, "0")}
                                        </span>
                                        <span className="text-sm font-medium text-[#172033]">{step.name}</span>
                                        {step.duration_ms ? (
                                            <span className="ml-auto text-[10px] text-[#94A3B8]">{step.duration_ms} ms</span>
                                        ) : null}
                                    </div>
                                    {step.detail && (
                                        <div className="text-xs text-[#64748B] mt-1 leading-snug">{step.detail}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Score gauges — 5 dimensions all valorized */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <RiskDimensionCard
                    label="Autenticità tecnica"
                    dimension={dimensions?.technical_authenticity || { score: pec.technical_score ?? null, status: pec.technical_score != null ? "computed" : "not_yet_computed" }}
                    mode="authenticity"
                    testId="gauge-technical"
                />
                <RiskDimensionCard
                    label="Affidabilità mittente"
                    dimension={dimensions?.sender_reliability}
                    mode="authenticity"
                    testId="gauge-sender"
                />
                <RiskDimensionCard
                    label="Rischio contenuto"
                    dimension={dimensions?.content_risk}
                    mode="risk"
                    testId="gauge-content"
                />
                <RiskDimensionCard
                    label="Rischio allegati/link"
                    dimension={dimensions?.attachments_links_risk}
                    mode="risk"
                    testId="gauge-attachments"
                />
                <RiskDimensionCard
                    label="Rischio operativo"
                    dimension={dimensions?.operational_risk}
                    mode="risk"
                    testId="gauge-operational"
                />
            </div>

            {/* Recommended action (from risk_scores) */}
            {(risk?.recommended_action || pec.recommendation) && (
                <div data-testid="recommended-action-card">
                    <AlertBanner
                        variant={
                            (risk?.operational_risk ?? 0) >= 80 ? "critico" :
                                (risk?.operational_risk ?? 0) >= 60 ? "alto" :
                                    (risk?.operational_risk ?? 0) >= 40 ? "attenzione" : "info"
                        }
                        title={`Azione consigliata${risk?.operational_risk != null ? ` · rischio operativo ${risk.operational_risk}/100` : ""}`}
                    >
                        <div>{risk?.recommended_action || pec.recommendation}</div>
                        {risk?.department_suggested && (
                            <div className="mt-1 text-xs opacity-80">Dipartimento suggerito: <strong>{risk.department_suggested}</strong></div>
                        )}
                    </AlertBanner>
                </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList data-testid="pec-analysis-tabs" className="bg-white border border-[#DCE3EE] flex-wrap h-auto">
                    <TabsTrigger value="overview" data-testid="tab-overview">Panoramica</TabsTrigger>
                    <TabsTrigger value="message" data-testid="tab-message">Messaggio</TabsTrigger>
                    <TabsTrigger value="technical" data-testid="tab-technical">Verifica tecnica</TabsTrigger>
                    <TabsTrigger value="attachments" data-testid="tab-attachments">
                        Allegati {attachments.length ? <Badge variant="secondary" className="ml-1.5 text-[10px]">{attachments.length}</Badge> : null}
                    </TabsTrigger>
                    <TabsTrigger value="links" data-testid="tab-links">
                        Link {links.length ? <Badge variant="secondary" className="ml-1.5 text-[10px]">{links.length}</Badge> : null}
                    </TabsTrigger>
                    <TabsTrigger value="headers" data-testid="tab-headers">Header</TabsTrigger>
                    <TabsTrigger value="xml" data-testid="tab-xml">XML PEC</TabsTrigger>
                    <TabsTrigger value="ai" data-testid="tab-ai">Analisi AI {aiAnalysis?.status === "ok" ? null : <Badge variant="secondary" className="ml-1.5 text-[10px]">off</Badge>}</TabsTrigger>
                    <TabsTrigger value="rules" data-testid="tab-rules">Regole {ruleExecs.length ? <Badge variant="secondary" className="ml-1.5 text-[10px]">{ruleExecs.length}</Badge> : null}</TabsTrigger>
                </TabsList>

                {/* OVERVIEW */}
                <TabsContent value="overview" className="mt-4 space-y-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-3">Sintesi tecnica</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <Field label="Stato tecnico" value={pec.technical_status} />
                            <Field label="Score autenticità" value={`${pec.technical_score ?? "—"}/100`} />
                            <Field label="Tipo busta" value={pec.envelope_type || "—"} />
                            <Field label="Message-ID" value={pec.message_id ? shortHash(pec.message_id) : "—"} mono />
                            <Field label="Allegati" value={String(pec.attachments_count || 0)} />
                            <Field label="Link estratti" value={String(pec.links_count || 0)} />
                            <Field label="Allegati sospetti" value={String(pec.suspicious_attachments_count || 0)} />
                            <Field label="SHA-256 file" value={shortHash(pec.sha256)} mono />
                        </div>
                    </Card>
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-3">Timeline</h3>
                        <div className="space-y-2 text-sm">
                            <TimelineRow icon={Mail} label="Messaggio ricevuto" value={formatDT(pec.date || pec.created_at)} />
                            <TimelineRow icon={Loader2} label="Analisi avviata" value={formatDT(pec.created_at)} />
                            <TimelineRow icon={CheckCircle2} label="Analisi completata" value={formatDT(pec.updated_at)} />
                        </div>
                    </Card>
                </TabsContent>

                {/* MESSAGE */}
                <TabsContent value="message" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-[#172033]">Corpo del messaggio</h3>
                            <div className="inline-flex bg-[#F5F7FB] border border-[#DCE3EE] rounded-md p-0.5">
                                <button
                                    onClick={() => setBodyMode("text")}
                                    data-testid="body-mode-text"
                                    className={`px-3 py-1 text-xs font-medium rounded ${bodyMode === "text" ? "bg-white shadow-sm text-[#172033]" : "text-[#64748B]"}`}
                                >Testo</button>
                                <button
                                    onClick={() => setBodyMode("html")}
                                    data-testid="body-mode-html"
                                    className={`px-3 py-1 text-xs font-medium rounded ${bodyMode === "html" ? "bg-white shadow-sm text-[#172033]" : "text-[#64748B]"}`}
                                >HTML (sandbox)</button>
                            </div>
                        </div>
                        {bodyMode === "text" ? (
                            <pre className="text-sm whitespace-pre-wrap text-[#172033] bg-[#F5F7FB] rounded-lg p-4 border border-[#DCE3EE] max-h-[500px] overflow-auto" data-testid="body-text">
                                {pec.body_text || "(Nessun corpo testo disponibile)"}
                            </pre>
                        ) : (
                            <div className="border border-[#DCE3EE] rounded-lg overflow-hidden bg-white">
                                <iframe
                                    title="Corpo HTML sandboxed"
                                    sandbox=""
                                    srcDoc={pec.body_html_sanitized || "<em>Nessun corpo HTML disponibile</em>"}
                                    className="w-full h-[500px] bg-white"
                                    data-testid="body-html-iframe"
                                />
                            </div>
                        )}
                    </Card>
                </TabsContent>

                {/* TECHNICAL */}
                <TabsContent value="technical" className="mt-4">
                    <div className="mb-4">
                        <SignatureVerificationBadge signatureVerification={data.signature_verification} />
                    </div>
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Check tecnici</h3>
                        <div className="space-y-2" data-testid="technical-checks-list">
                            {checks.length === 0 && <div className="text-sm text-[#64748B]">Nessun check disponibile.</div>}
                            {checks.map((c) => (
                                <div key={c.id} className="flex items-start gap-3 p-3 rounded-lg border border-[#DCE3EE] bg-[#F5F7FB]/50">
                                    <CheckIcon status={c.status} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-[#172033]">{c.label}</div>
                                        {c.detail && <div className="text-xs text-[#64748B] mt-0.5">{c.detail}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>

                {/* ATTACHMENTS */}
                <TabsContent value="attachments" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Allegati ({attachments.length})</h3>
                        {attachments.length === 0 ? (
                            <div className="text-sm text-[#64748B]">Nessun allegato.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="attachments-table">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-[#DCE3EE]">
                                            <th className="py-2">Nome</th>
                                            <th className="py-2">MIME dichiarato</th>
                                            <th className="py-2">MIME reale</th>
                                            <th className="py-2">Dimensione</th>
                                            <th className="py-2">SHA-256</th>
                                            <th className="py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attachments.map((a) => (
                                            <tr key={a.id} className="border-b border-[#DCE3EE] last:border-0">
                                                <td className="py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <Paperclip className="w-4 h-4 text-[#64748B]" />
                                                        <div>
                                                            <div className="text-[#172033] font-medium">{a.filename}</div>
                                                            <div className="text-[10px] text-[#64748B] flex items-center gap-1">
                                                                {a.is_certification && <Badge variant="outline" className="text-[9px] py-0">daticert</Badge>}
                                                                {a.is_signature && <Badge variant="outline" className="text-[9px] py-0">firma S/MIME</Badge>}
                                                                {a.double_extension && <Badge className="text-[9px] py-0 bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30">estensione doppia</Badge>}
                                                                {a.mime_mismatch && <Badge className="text-[9px] py-0 bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30">MIME mismatch</Badge>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-2.5 text-xs font-mono text-[#64748B]">{a.declared_mime}</td>
                                                <td className="py-2.5 text-xs font-mono text-[#172033]">{a.real_mime}</td>
                                                <td className="py-2.5 text-xs text-[#64748B]">{a.size < 1024 ? a.size + " B" : (a.size / 1024).toFixed(1) + " KB"}</td>
                                                <td className="py-2.5 text-xs font-mono text-[#64748B]" title={a.sha256}>{shortHash(a.sha256)}</td>
                                                <td className="py-2.5 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-[#2563EB]"
                                                        data-testid={`download-att-${a.id}`}
                                                        onClick={() => downloadAuthenticated(`/attachments/${a.id}/download`, a.filename)}
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                {/* LINKS */}
                <TabsContent value="links" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Link estratti ({links.length})</h3>
                        <AlertBanner variant="attenzione" title="Attenzione">
                            I link non vengono aperti automaticamente. Usare "Copia URL" per la verifica manuale.
                            L'analisi di rischio dei link sarà disponibile in Fase 3.
                        </AlertBanner>
                        {links.length === 0 ? (
                            <div className="text-sm text-[#64748B] mt-4">Nessun link estratto.</div>
                        ) : (
                            <div className="mt-4 space-y-2" data-testid="links-list">
                                {links.map((l) => (
                                    <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#DCE3EE] bg-[#F5F7FB]/50">
                                        <LinkIcon className="w-4 h-4 text-[#64748B] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-mono text-[#172033] truncate" title={l.url}>{l.url}</div>
                                            <div className="text-xs text-[#64748B] flex items-center gap-2 mt-0.5">
                                                <span>{l.host}</span>
                                                {l.is_https ? (
                                                    <Badge className="text-[9px] py-0 bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30">HTTPS</Badge>
                                                ) : (
                                                    <Badge className="text-[9px] py-0 bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30">HTTP</Badge>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid={`copy-link-${l.id}`}
                                            onClick={() => {
                                                navigator.clipboard.writeText(l.url);
                                                toast.success("URL copiato negli appunti");
                                            }}
                                        >
                                            <Copy className="w-3.5 h-3.5 mr-1" /> Copia URL
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                {/* HEADERS */}
                <TabsContent value="headers" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Intestazioni originali</h3>
                        {headers?.duplicated_headers?.length > 0 && (
                            <AlertBanner variant="attenzione" className="mb-3">
                                Header duplicati rilevati: {headers.duplicated_headers.join(", ")}
                            </AlertBanner>
                        )}
                        <pre className="text-xs font-mono bg-[#0B1633] text-[#94A3B8] rounded-lg p-4 max-h-[500px] overflow-auto" data-testid="headers-dump">
                            {headers?.headers
                                ? Object.entries(headers.headers).map(([k, v]) => `${k}: ${v}`).join("\n")
                                : "Nessun header disponibile."}
                        </pre>
                    </Card>
                </TabsContent>

                {/* XML */}
                <TabsContent value="xml" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Busta di certificazione PEC</h3>
                        {!daticert ? (
                            <div className="text-sm text-[#64748B]">Nessuna busta daticert.xml disponibile.</div>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    <Field label="Tipo busta" value={daticert.envelope_type} />
                                    <Field label="Mittente" value={daticert.sender_xml} />
                                    <Field label="Message-ID (XML)" value={daticert.msg_id_xml || "—"} mono />
                                    <Field label="Data giorno" value={`${daticert.date_giorno || "—"} ${daticert.date_ora || ""} ${daticert.date_zona || ""}`} />
                                    <Field label="Oggetto XML" value={daticert.subject_xml || "—"} />
                                    <Field label="Consegna" value={daticert.delivery_note || "—"} />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold mb-1">Destinatari XML</div>
                                    <div className="text-sm">
                                        {(daticert.recipients_xml || []).map((r, i) => (
                                            <div key={i} className="font-mono text-xs">{r.address} <span className="text-[#94A3B8]">({r.type})</span></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                {/* AI Analysis */}
                <TabsContent value="ai" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]" data-testid="ai-analysis-card">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Analisi AI del contenuto</h3>
                        {!aiAnalysis ? (
                            <div className="text-sm text-[#64748B]">Analisi AI non disponibile per questa PEC (AI potrebbe essere disabilitata per l'azienda).</div>
                        ) : aiAnalysis.status !== "ok" ? (
                            <AlertBanner variant="attenzione">Analisi AI non riuscita: {aiAnalysis.error_detail || "errore sconosciuto"}</AlertBanner>
                        ) : (
                            <div className="space-y-4">
                                <div className="text-xs text-[#94A3B8]">Model: {aiAnalysis.model}</div>
                                <Field label="Riassunto" value={aiAnalysis.data?.summary} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label="Intento" value={aiAnalysis.data?.intent} />
                                    <Field label="Azione richiesta" value={aiAnalysis.data?.requested_action || "—"} />
                                    <Field label="Deadline" value={aiAnalysis.data?.deadline || "—"} />
                                    <Field label="Dipartimento suggerito" value={aiAnalysis.data?.recommended_department} />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold mb-2">Segnali di rischio</div>
                                    <div className="flex flex-wrap gap-2">
                                        {(aiAnalysis.data?.risk_signals || []).length === 0 && <span className="text-xs text-[#64748B]">Nessuno</span>}
                                        {(aiAnalysis.data?.risk_signals || []).map((s, i) => (
                                            <Badge key={i} className="bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30">{s}</Badge>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {["amounts", "ibans", "invoice_numbers", "companies", "people", "phone_numbers", "email_addresses", "urls"].map(k => (
                                        <div key={k}>
                                            <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">{k}</div>
                                            <div className="text-xs text-[#172033] font-mono break-all">
                                                {(aiAnalysis.data?.[k] || []).length === 0 ? "—" : (aiAnalysis.data?.[k] || []).join(", ")}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                {/* Rules triggered */}
                <TabsContent value="rules" className="mt-4">
                    <Card className="p-5 border-[#DCE3EE]" data-testid="rules-triggered-card">
                        <h3 className="text-sm font-semibold text-[#172033] mb-4">Regole attivate ({ruleExecs.length})</h3>
                        {ruleExecs.length === 0 ? (
                            <div className="text-sm text-[#64748B]">Nessuna regola attivata per questa PEC.</div>
                        ) : (
                            <div className="space-y-2">
                                {ruleExecs.map((ex) => {
                                    const rule = rulesInfo.find(r => r.id === ex.rule_id);
                                    return (
                                        <div key={ex.id} className="flex items-start gap-3 p-3 rounded-lg border border-[#DCE3EE] bg-[#F5F7FB]/50" data-testid={`rule-exec-${ex.id}`}>
                                            <CheckCircle2 className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold text-[#172033]">{rule?.name || ex.rule_id}</div>
                                                <div className="text-xs text-[#64748B]">{rule?.description}</div>
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {ex.actions_executed?.map((a, i) => <Badge key={i} variant="outline" className="text-[10px]">{a}</Badge>)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Field({ label, value, mono = false }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</div>
            <div className={`text-sm text-[#172033] ${mono ? "font-mono" : "font-medium"}`}>{value || "—"}</div>
        </div>
    );
}

function TimelineRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#2563EB]" />
            </div>
            <div>
                <div className="text-sm font-medium text-[#172033]">{label}</div>
                <div className="text-xs text-[#64748B]">{value}</div>
            </div>
        </div>
    );
}

function CheckIcon({ status }) {
    if (status === "ok") return <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0" />;
    if (status === "fail") return <XCircle className="w-5 h-5 text-[#DC2626] shrink-0" />;
    if (status === "warn") return <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0" />;
    return <Info className="w-5 h-5 text-[#64748B] shrink-0" />;
}
